import Dexie, { Table } from "dexie"

import {
  decryptSensitiveValues,
  DraftKeyRecord,
  EncryptedSensitiveValues,
  encryptSensitiveValues,
  getOrCreateDraftKey,
  INTAKE_FORM_SENSITIVE_TTL_MS,
  restoreSensitiveValue,
  takeSensitiveValue,
} from "@/lib/intake-form-draft-crypto"

export const INTAKE_FORM_DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000

export interface IntakeFormDraftNav {
  activeStepKey: string | null
  completed: Record<string, boolean>
  focusKey?: string | null
  focusStarted?: boolean
}

export interface IntakeFormDraft {
  key: string
  versionId: string
  answers: Record<string, unknown>
  attestations: Record<string, string>
  touchedKeys: string[]
  nav: IntakeFormDraftNav
  clientSubmissionId: string | null
  uploadedBlobs: Record<string, string>
  // SSN values never persist in answers: they are pulled out by path, AES-GCM
  // encrypted, and kept on a much shorter TTL than the rest of the draft. The
  // paths themselves are stored so a draft reloaded via getDraft (which merges
  // the decrypted values back into answers) re-strips them when rewritten.
  sensitivePaths?: string[]
  sensitive?: EncryptedSensitiveValues | null
  sensitiveExpiresAt?: number | null
  updatedAt: number
  expiresAt: number
}

export type IntakeFormDraftInput = Omit<IntakeFormDraft, "updatedAt" | "expiresAt" | "sensitive" | "sensitiveExpiresAt">

export const publicationDraftKey = (token: string) => `p:${token}`

class IntakeFormDraftDatabase extends Dexie {
  drafts!: Table<IntakeFormDraft>
  keys!: Table<DraftKeyRecord>

  constructor() {
    super("slant-intake-form-drafts")

    this.version(1).stores({
      drafts: "key, expiresAt",
    })
    this.version(2).stores({
      drafts: "key, expiresAt",
      keys: "id",
    })
  }
}

let database: IntakeFormDraftDatabase | null = null

// Every entry point degrades to "no resume" rather than throwing: private browsing
// modes and storage-partitioned contexts can refuse IndexedDB outright, and a form
// that cannot save progress must still be fillable.
function draftDb(): IntakeFormDraftDatabase | null {
  if (typeof window === "undefined") return null
  if (database) return database
  try {
    database = new IntakeFormDraftDatabase()
  } catch {
    return null
  }
  return database
}

function isBlankValue(value: unknown): boolean {
  if (value == null) return true
  if (value === false) return true
  if (typeof value === "string") return value.trim() === ""
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === "object") return Object.values(value).every((entry) => entry == null || entry === "")
  return false
}

function hasContent(draft: IntakeFormDraftInput): boolean {
  return Object.values(draft.answers).some((value) => !isBlankValue(value))
}

export async function getDraft(key: string): Promise<IntakeFormDraft | null> {
  const db = draftDb()
  if (!db) return null

  try {
    const draft = await db.drafts.get(key)
    if (!draft) return null
    if (draft.expiresAt <= Date.now()) {
      await db.drafts.delete(key)
      return null
    }
    return await withRestoredSensitiveValues(db, draft)
  } catch {
    return null
  }
}

async function withRestoredSensitiveValues(
  db: IntakeFormDraftDatabase,
  draft: IntakeFormDraft
): Promise<IntakeFormDraft> {
  if (!draft.sensitive) return draft
  if (!draft.sensitiveExpiresAt || draft.sensitiveExpiresAt <= Date.now()) return scrubSensitiveValues(db, draft)

  const key = await getOrCreateDraftKey(db.keys)
  const values = key ? await decryptSensitiveValues(key, draft.sensitive) : null
  if (!values) return scrubSensitiveValues(db, draft)

  const answers = JSON.parse(JSON.stringify(draft.answers)) as Record<string, unknown>
  Object.entries(values).forEach(([path, value]) => restoreSensitiveValue(answers, path, value))
  return { ...draft, answers, sensitive: null, sensitiveExpiresAt: null }
}

// The two-hour retention promise has to hold at rest, not just in memory —
// expired (or undecryptable) ciphertext is removed from the stored record the
// first time it is read.
async function scrubSensitiveValues(db: IntakeFormDraftDatabase, draft: IntakeFormDraft): Promise<IntakeFormDraft> {
  const scrubbed: IntakeFormDraft = { ...draft, sensitive: null, sensitiveExpiresAt: null }
  try {
    await db.drafts.put(scrubbed)
  } catch {
    // The record keeps its ciphertext until the next successful write or the
    // draft's own expiry — never block loading the form on cleanup.
  }
  return scrubbed
}

export async function putDraft(
  draft: IntakeFormDraftInput,
  sensitivePaths?: string[]
): Promise<IntakeFormDraft | null> {
  const db = draftDb()
  // An all-blank draft is never worth a record — except once a submit attempt has
  // minted a clientSubmissionId, which must survive reloads so a retried submission
  // stays idempotent (file-only forms have blank answers by design).
  if (!db || (!hasContent(draft) && !draft.clientSubmissionId)) return null

  // Callers rewriting a loaded draft (e.g. pinning it to a newer version) don't
  // know the schema, so fall back to the paths the record already carries —
  // getDraft restored those values into answers and they must be re-stripped.
  const effectivePaths = sensitivePaths ?? draft.sensitivePaths ?? []
  const updatedAt = Date.now()
  const record: IntakeFormDraft = {
    ...draft,
    sensitivePaths: effectivePaths,
    sensitive: null,
    sensitiveExpiresAt: null,
    updatedAt,
    expiresAt: updatedAt + INTAKE_FORM_DRAFT_TTL_MS,
  }
  await stripSensitiveValues(db, record, effectivePaths)

  try {
    await db.drafts.put(record)
    // Callers get the in-memory shape (raw answers, no ciphertext) — the same
    // contract as getDraft — so a rewritten draft can remount the form without
    // blanking the values that were just encrypted at rest.
    return { ...record, answers: draft.answers, sensitive: null, sensitiveExpiresAt: null }
  } catch {
    return null
  }
}

// Pulls each sensitive path's value out of the stored answers into an
// encrypted blob with its own short expiry. When encryption is unavailable the
// values are dropped entirely — a resumed draft then just has a blank SSN.
async function stripSensitiveValues(
  db: IntakeFormDraftDatabase,
  record: IntakeFormDraft,
  sensitivePaths: string[]
): Promise<void> {
  if (sensitivePaths.length === 0) return

  const answers = JSON.parse(JSON.stringify(record.answers)) as Record<string, unknown>
  const values: Record<string, unknown> = {}
  sensitivePaths.forEach((path) => {
    const value = takeSensitiveValue(answers, path)
    if (value !== undefined) values[path] = value
  })
  record.answers = answers
  if (Object.keys(values).length === 0) return

  const key = await getOrCreateDraftKey(db.keys)
  const encrypted = key ? await encryptSensitiveValues(key, values) : null
  if (!encrypted) return

  record.sensitive = encrypted
  record.sensitiveExpiresAt = Date.now() + INTAKE_FORM_SENSITIVE_TTL_MS
}

export async function deleteDraft(key: string): Promise<void> {
  const db = draftDb()
  if (!db) return

  try {
    await db.drafts.delete(key)
  } catch {
    // A draft we cannot delete expires on its own within the TTL window.
  }
}

export async function purgeExpiredDrafts(): Promise<void> {
  const db = draftDb()
  if (!db) return

  try {
    await db.drafts.where("expiresAt").below(Date.now()).delete()
    // Also sweep expired sensitive ciphertext out of drafts that are otherwise
    // still alive — the two-hour bound must not depend on that exact draft
    // being reopened before its 14-day expiry.
    await db.drafts
      .filter((draft) => Boolean(draft.sensitive) && (draft.sensitiveExpiresAt ?? 0) <= Date.now())
      .modify((draft) => {
        draft.sensitive = null
        draft.sensitiveExpiresAt = null
      })
  } catch {
    // Purging is opportunistic housekeeping — never block loading a form on it.
  }
}
